import type { ReactNode } from 'react';
import ImageView from '../../../../components/ui/ImageView/ImageView';
import utilsStyles from '../../../../styles/modules/utils.module.scss';
import type { LotteryOptionGoodsForm } from '../../types';
import styles from './OptionNamedCollectionList.module.scss';

type OptionNamedCollectionListProps = {
  formItem: LotteryOptionGoodsForm;
  optionInfo: LotteryOptionGoodsForm['optionInfo'];
  editMode: boolean;
  isDeadlinePassed: boolean;
  inputErrors: Record<string, boolean>;
  onInputChange: (inputKey: string, value: string) => void;
  getNamedInputPlaceholder: () => string;
  shouldShowAutoInputCount: () => boolean;
  invalidCharacterErrorMessage: string;
  staging: (formItem: LotteryOptionGoodsForm) => ReactNode;
};

function OptionNamedCollectionList({
  formItem,
  optionInfo,
  editMode,
  isDeadlinePassed,
  inputErrors,
  onInputChange,
  getNamedInputPlaceholder,
  shouldShowAutoInputCount,
  invalidCharacterErrorMessage,
  staging,
}: OptionNamedCollectionListProps) {
  return (
    <div key={`named-${formItem.id}`} className={`${styles.itemCardNamed}`}>
      <div className={styles.itemCardNamedInner}>
        <div className={`${styles.itemDetailNamedWrapper} ${utilsStyles.mtXSmall}`}>
          <div className={styles.itemImage}>
            <ImageView alt={formItem.name} src={formItem.imageUrl} />
          </div>
          <div className={`${styles.itemDetailNamed} ${utilsStyles.mlSmall}`}>
            <div className={styles.stagingRankWrapper}>{staging(formItem)}</div>
            <div className={styles.itemNameWrapper}>
              <p className={`${styles.itemName} ${utilsStyles.textBold}`}>{formItem.label.name}</p>
              <p className={`${styles.itemName} ${utilsStyles.textBold}`}>{formItem.name}</p>
            </div>
            <div className={`${styles.autoInputMessageNamedWrapper} ${utilsStyles.mtXSmall} `}>
              {shouldShowAutoInputCount() && (
                <div
                  className={`${utilsStyles.textSmall} ${utilsStyles.textBold}  ${styles.autoInputMessage} ${styles.autoInputMessageRight}`}
                >
                  自動入力数：
                  {formItem.optionInfo.named?.filter((item) => item.isSystemInput === true).length || 0}個
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={styles.optionInputNamedWrapper}>
          {formItem.totalAcquiredCount > 1 && (
            <p className={`${utilsStyles.mtXSmall} ${utilsStyles.textSmall} ${utilsStyles.textBold}`}>
              ※同一の内容をご希望の場合も、
              <span className={`${utilsStyles.primaryColor} ${utilsStyles.textBold}`}>入力欄１個につき１点</span>
              の入力をお願いいたします
            </p>
          )}
          {Array.from({ length: formItem.totalAcquiredCount }, (_, inputIndex) => {
            // 既存の入力内容を取得
            const namedItem = optionInfo.named?.[inputIndex];
            const existingValue = namedItem?.value || '';
            const isSystemInput = namedItem?.isSystemInput || false;
            const inputKey = `named-${formItem.id}-${inputIndex}`;
            const hasError = inputErrors[inputKey];
            const displayValue =
              isDeadlinePassed && isSystemInput && existingValue ? `【自動入力】${existingValue}` : existingValue;
            const optionInputTitle = `オプション入力欄（${inputIndex + 1}/${formItem.totalAcquiredCount}）`;

            return (
              <div key={`${formItem.id}-${inputIndex}`} className={styles.inputContainer}>
                <div className={`${utilsStyles.mtXSmall} ${utilsStyles.textSmall} ${utilsStyles.textBold}`}>
                  {optionInputTitle}
                </div>
                {hasError && <div className={styles.errorMessage}>{invalidCharacterErrorMessage}</div>}
                <textarea
                  key={`input-${formItem.id}-${inputIndex}-${existingValue}`}
                  name={`optionInput-${formItem.id}-${inputIndex}`}
                  placeholder={getNamedInputPlaceholder()}
                  className={`${styles.optionInput} ${editMode && isDeadlinePassed ? styles.editMode : ''} ${hasError ? styles.inputError : ''} ${isDeadlinePassed && isSystemInput ? styles.systemInput : ''}`}
                  defaultValue={displayValue}
                  onChange={(e) => onInputChange(inputKey, e.target.value)}
                  disabled={editMode && isDeadlinePassed}
                  rows={3}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default OptionNamedCollectionList;
